import React from 'react';
import { Link } from 'react-router-dom';
import {
  Menu,
  Sun,
  Moon,
  Monitor,
  Settings,
  Wrench,
  LogOut,
  Search,
  FileText,
  Shield,
  User,
} from 'lucide-react';
import { useAuth } from '../../auth/AuthProvider';
import { useTheme } from '../../providers/ThemeProvider';
import { useBreadcrumbs } from '../../providers/BreadcrumbProvider';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../ui/breadcrumb';
import { cn } from '../../lib/utils';

interface HeaderProps {
  onMenuClick: () => void;
}

function getInitials(name?: string): string | null {
  if (!name) return null;
  const initials = name.split(' ').map(n => n[0]).filter(Boolean).join('').toUpperCase().slice(0, 2);
  return initials || null;
}

export default function Header({ onMenuClick }: HeaderProps) {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const { items: breadcrumbs } = useBreadcrumbs();

  const themeIcon = theme === 'dark' ? Sun : theme === 'light' ? Moon : Monitor;
  const ThemeIcon = themeIcon;

  const cycleTheme = () => {
    if (theme === 'light') setTheme('dark');
    else if (theme === 'dark') setTheme('system');
    else setTheme('light');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-30 glass-heavy border-b border-border/50">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Left: Mobile menu + Logo + Breadcrumbs */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="lg:hidden p-2 rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
              onClick={onMenuClick}
            >
              <Menu className="h-5 w-5" />
            </button>

            {/* Logo */}
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg gradient-primary shadow-sm">
                <span className="text-white font-bold text-sm">P8</span>
              </div>
              <div className="hidden sm:block">
                <h1 className="text-base font-semibold text-foreground leading-tight">
                  Pax8 Licensing
                </h1>
              </div>
            </div>

            {/* Breadcrumbs (desktop only) */}
            <div className="hidden lg:flex items-center ml-4">
              <div className="w-px h-6 bg-border/60 mr-4" />
              <Breadcrumb>
                <BreadcrumbList>
                  {breadcrumbs.map((crumb, i) => (
                    <React.Fragment key={i}>
                      {i > 0 && <BreadcrumbSeparator />}
                      <BreadcrumbItem>
                        {crumb.href ? (
                          <BreadcrumbLink asChild>
                            <Link to={crumb.href}>{crumb.label}</Link>
                          </BreadcrumbLink>
                        ) : (
                          <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                        )}
                      </BreadcrumbItem>
                    </React.Fragment>
                  ))}
                </BreadcrumbList>
              </Breadcrumb>
            </div>
          </div>

          {/* Right: Search + Theme + User */}
          <div className="flex items-center gap-1 sm:gap-2">
            {/* Cmd+K Search Trigger */}
            <button
              onClick={() => {
                document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }));
              }}
              className={cn(
                "hidden sm:inline-flex items-center gap-2 h-9 rounded-md border border-input bg-background/50 px-3 text-sm text-muted-foreground",
                "hover:bg-accent hover:text-accent-foreground transition-colors"
              )}
            >
              <Search className="h-4 w-4" />
              <span className="hidden md:inline">Search...</span>
              <kbd className="hidden md:inline-flex pointer-events-none h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                <span className="text-xs">⌘</span>K
              </kbd>
            </button>

            {/* Mobile search */}
            <button
              onClick={() => {
                document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }));
              }}
              className="sm:hidden p-2 rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              <Search className="h-5 w-5" />
            </button>

            {/* Theme Toggle */}
            <button
              onClick={cycleTheme}
              className="p-2 rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
              title={`Theme: ${theme}`}
            >
              <ThemeIcon className="h-5 w-5" />
            </button>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-md p-1.5 hover:bg-accent transition-colors">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-primary-600/10 text-primary-600 dark:bg-primary-500/20 dark:text-primary-400 text-xs font-semibold">
                      {getInitials(user?.name || user?.username) || <User className="h-4 w-4" />}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden md:block text-sm font-medium text-foreground max-w-[120px] truncate">
                    {user?.name || user?.username}
                  </span>
                </button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">{user?.name}</p>
                    <p className="text-xs leading-none text-muted-foreground truncate">
                      {user?.username}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/changelog" className="flex items-center gap-2 cursor-pointer">
                    <FileText className="h-4 w-4" />
                    Changelog
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/settings/user" className="flex items-center gap-2 cursor-pointer">
                    <Settings className="h-4 w-4" />
                    My Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings/system" className="flex items-center gap-2 cursor-pointer">
                    <Wrench className="h-4 w-4" />
                    System Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings/security" className="flex items-center gap-2 cursor-pointer">
                    <Shield className="h-4 w-4" />
                    Security Collection
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="cursor-pointer">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
}
